import { environment } from '../../../../../src/environments/environment'
import axios, { AxiosResponse } from 'axios'
import { Injectable } from '@angular/core';
import { ResponseModel } from './model/ResponseModel';
import { PhicubeResponse } from './interface/PhicubeResponse';
import { ThrowError } from './ThrowError';

import '@capacitor-community/http';
import { Http, HttpResponse } from '@capacitor-community/http';
import { isPlatform } from '@ionic/core';
import { MessageService } from './MessageService';
import { Subject } from 'rxjs';
import { AppSession } from './AppSession';
import { AppStorage } from './AppStorage';
 
@Injectable({
    providedIn: 'root'
})
export class GatewayService {

    public static TIME_OUT = 10000
    tentativaErro = 1
    quantidadeTentativas = 4

    constructor() {

    }

    getBaseUrl() {
        return environment.pathToMobile
    }

    getClientId() {
        return environment.clientId
    }

    getSecretId() {
        return environment.clientSecret
    }

    getUrlApi(rota: String) {
        return this.getBaseUrl() + rota
    }

    async axiosInstance(rotaPublica: Boolean = false) {
        return axios.create({
            baseURL: this.getBaseUrl(),
            timeout: GatewayService.TIME_OUT,
            headers: await this.getHeaders(rotaPublica),
            validateStatus: (code: Number) => {
                return true
            }
        });
    }

    async requestGet(rota: string, rotaPublica: Boolean = false) {
        let retorno
        if (!isPlatform("capacitor")) {
            retorno = await this.trataRetornoAxios((await this.axiosInstance(rotaPublica)).get(rota), rotaPublica)
        } else {
            retorno = await this.trataRetornoHttp(this.getBaseUrl() + rota, 'get', await this.getHeaders(rotaPublica), rotaPublica)
        }
        let phicubeResponse = new PhicubeResponse()
        phicubeResponse.data = retorno.data
        phicubeResponse.status = retorno.status
        return phicubeResponse
    }

    async requestDel(rota: string, rotaPublica: Boolean = false) {
        let retorno
        if (!isPlatform("capacitor")) {
            retorno = await this.trataRetornoAxios((await this.axiosInstance(rotaPublica)).delete(rota), rotaPublica)
        } else {
            retorno = await this.trataRetornoHttp(this.getBaseUrl() + rota, 'delete', await this.getHeaders(rotaPublica), rotaPublica)
        }
        let phicubeResponse = new PhicubeResponse()
        phicubeResponse.data = retorno.data
        phicubeResponse.status = retorno.status
        return phicubeResponse
    }

    async requestPost(rota: string, data: Object, rotaPublica: Boolean = false) {
        let retorno
        if (!isPlatform("capacitor")) {
            retorno = await this.trataRetornoAxios((await this.axiosInstance(rotaPublica)).post(rota, data), rotaPublica)
        } else {
            retorno = await this.trataRetornoHttp(this.getBaseUrl() + rota, 'post', await this.getHeaders(rotaPublica), rotaPublica, data)
        }
        let phicubeResponse = new PhicubeResponse()
        phicubeResponse.data = retorno.data
        phicubeResponse.status = retorno.status
        return phicubeResponse
    }

    async requestPut(rota: string, data: Object, rotaPublica: Boolean = false) {
        let retorno
        if (!isPlatform("capacitor")) {
            retorno = await this.trataRetornoAxios((await this.axiosInstance(rotaPublica)).put(rota, data), rotaPublica)
        } else {
            retorno = await this.trataRetornoHttp(this.getBaseUrl() + rota, 'put', await this.getHeaders(rotaPublica), rotaPublica, data)
        }
        let phicubeResponse = new PhicubeResponse()
        phicubeResponse.data = retorno.data
        phicubeResponse.status = retorno.status
        return phicubeResponse
    }

    async trataRetornoAxios(promise: Promise<AxiosResponse>, rotaPublic: Boolean = false) {
        try {
            const retorno = await promise
            if (retorno.status == ResponseModel.STATUS_UNAUTHORIZED && !rotaPublic) {
                const refreshToken = await this.requestToken()
                if (refreshToken.status == ResponseModel.STATUS_OK) {
                    await AppSession.saveToken(refreshToken.data)
                    retorno.config.headers = await this.getHeaders(rotaPublic)
                    return await this.trataRetornoAxios(axios.request(retorno.config), rotaPublic)
                } else {
                    await AppSession.clearSession()
                    MessageService.sendMessage(MessageService.KEY_LOGOUT_FOR_INVALID_TOKEN)
                }
            } else {
                this.resetTentativaErro()
                return retorno
            }
        } catch(e) {
            if (this.tentativaErro <= this.quantidadeTentativas) {
                await this.requestTry()
                this.tentativaErro++
                await this.trataRetornoAxios(promise, rotaPublic)
            } else {
                this.resetTentativaErro()
                throw Error(ThrowError.MESSAGE_DEFAULT)
            }
        }
    }

    async trataRetornoHttp(
        url,
        method: 'get' | 'post' | 'put' | 'patch' | 'head' | 'delete' | 'options' | 'upload' | 'download',
        headers,
        publicRoute: Boolean = false,
        data = {}
    ) {
        try {
            let body: any = { url, method, data, headers }
            if (method == "get") {
                body = { url, method, headers }
            }
            const retorno = await Http.request(body)
            if (retorno.status == ResponseModel.STATUS_OK && !publicRoute && retorno.url.indexOf("/login") >= 0) {
                const refreshToken = await this.requestToken()
                if (refreshToken.status == ResponseModel.STATUS_OK) {
                    await AppSession.saveToken(refreshToken.data)
                    return await this.trataRetornoHttp(url, method, {...headers, ...await this.getHeaders(publicRoute)}, publicRoute, data)
                } else {
                    await AppSession.clearSession()
                    MessageService.sendMessage(MessageService.KEY_LOGOUT_FOR_INVALID_TOKEN)
                }
            } else {
                this.resetTentativaErro()
                return retorno
            }
        } catch(e) {
            if (this.tentativaErro <= this.quantidadeTentativas) {
                await this.requestTry()
                this.tentativaErro++
                await this.trataRetornoHttp(url, method, headers, publicRoute, data)
            } else {
                this.resetTentativaErro()
                throw Error(ThrowError.MESSAGE_DEFAULT)
            }
        }
    }

    async requestTry() {
        return new Promise((resolve, reject) => {
            setTimeout(() => {    
                resolve({})
            }, this.tentativaErro * 1000)
        })
    }

    private resetTentativaErro() {
        this.tentativaErro = 1
    }

    private async getHeaders(rotaPublica: Boolean = false): Promise<any> {
        let headers = {}
        headers["Content-Type"] = 'application/json'
        const token = await AppSession.getToken()
        if (!rotaPublica && token) {
            headers["Authorization"] = token.token_type + ' ' + token.access_token
        }
        return headers
    }

    async requestToken() {
        let isWeb = await AppStorage.get(AppSession.CHAVE_IS_WEB)
        if (isWeb == true) {
            return {
                status: 0,
                data: null
            }
        }
        const data = {
            "grant_type": "refresh_token",
            "client_id": environment.clientId,
            "client_secret": environment.clientSecret,
            "refresh_token": (await AppSession.getToken()).refresh_token
        }
        return await (await this.axiosInstance(true)).post('/api/oauth/token', data)
    }
}